/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { QueryFilters } from '@atlassian/audit-logs-common';

import { DetailsRow } from '../../../../common/utils/components/details-row';
import { DetailsSection } from '../../../../common/utils/components/details-section';

const messages = defineMessages({
	filtersApplied: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.title',
		defaultMessage: 'Filters applied',
		description: 'Title for the filters applied section in query details panel',
	},
	date: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.date.label',
		defaultMessage: 'Date',
		description: 'Label for the date filter field',
	},
	activity: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.activity.label',
		defaultMessage: 'Activity',
		description: 'Label for the activity filter field',
	},
	actor: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.actor.label',
		defaultMessage: 'Actor',
		description: 'Label for the actor filter field',
	},
	ipAddress: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.ip-address.label',
		defaultMessage: 'IP Address',
		description: 'Label for the IP address filter field',
	},
	location: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.location.label',
		defaultMessage: 'Location',
		description: 'Label for the location filter field',
	},
	app: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.app.label',
		defaultMessage: 'App',
		description: 'Label for the app filter field',
	},
	relatedActivities: {
		id: 'organization.audit.log.side-panel.tab-panels.query-details.filters-section.related-activities.label',
		defaultMessage: 'Related activities',
		description: 'Label for the related activities filter field',
	},
});

interface FiltersSectionProps {
	filters?: QueryFilters;
}

export const FiltersSection = ({ filters }: FiltersSectionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!filters) {
		return null;
	}

	const { event_time, activity, actors, ips, location, app, related_activities } = filters;

	// Check if any filter is present with non-empty values
	const hasFilters =
		event_time ||
		(activity && activity.length > 0) ||
		(actors && actors.length > 0) ||
		(ips && ips.length > 0) ||
		(location && location.length > 0) ||
		(app && app.length > 0) ||
		(related_activities && related_activities.length > 0);

	if (!hasFilters) {
		return null;
	}

	return (
		<DetailsSection title={formatMessage(messages.filtersApplied)}>
			{event_time && (
				<DetailsRow
					label={formatMessage(messages.date)}
					value={`${event_time.from} - ${event_time.to}`}
				/>
			)}
			{activity && activity.length > 0 && (
				<DetailsRow label={formatMessage(messages.activity)} value={activity.join(', ')} />
			)}
			{actors && actors.length > 0 && (
				<DetailsRow label={formatMessage(messages.actor)} value={actors.join(', ')} />
			)}
			{ips && ips.length > 0 && (
				<DetailsRow label={formatMessage(messages.ipAddress)} value={ips.join(', ')} />
			)}
			{location && location.length > 0 && (
				<DetailsRow label={formatMessage(messages.location)} value={location.join(', ')} />
			)}
			{app && app.length > 0 && (
				<DetailsRow label={formatMessage(messages.app)} value={app.join(', ')} />
			)}
			{related_activities && related_activities.length > 0 && (
				<DetailsRow
					label={formatMessage(messages.relatedActivities)}
					value={related_activities.join(', ')}
				/>
			)}
		</DetailsSection>
	);
};
